/**
 * @file Userbox.index.jsx
 * @description
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Sep 05, 2023 14:47
 */
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    Avatar,
    Box,
    Button,
    Divider,
    MenuList,
    alpha,
    IconButton,
    MenuItem,
    ListItemText,
    Popover,
    Typography,
    styled,
    useTheme
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';

import notFinishedMsg from 'src/@common/notFinishedMsg';

import { logout } from 'src/content/Auth/authUtilities';

const DotLegend = styled('span')(
    ({ theme }) => `
    border-radius: 22px;
    width: ${theme.spacing(1.38)};
    height: ${theme.spacing(1.4)};
    display: inline-block;
    border: ${theme.colors.alpha.white[100]} solid 2px;
`
);

const UserBoxButton = styled(IconButton)(
    ({ theme }) => `
  width: ${theme.spacing(4)};
  padding: 0;
  height: ${theme.spacing(4)};
  margin-left: ${theme.spacing(1)};
  border-radius: ${theme.general.borderRadiusLg};
  
  &:hover {
    background: ${theme.colors.primary.main};
  }
`
);

const UserAvatar = styled(Avatar)(
    ({ theme }) => `
        height: 90%;
        width: 90%;
        border-radius: ${theme.general.borderRadiusLg};
`
);

const MenuListWrapperPrimary = styled(MenuList)(
    ({ theme }) => `
  padding: ${theme.spacing(2)};

  & .MuiMenuItem-root {
      border-radius: 50px;
      padding: ${theme.spacing(1, 1, 1, 2.5)};
      min-width: 200px;
      margin-bottom: 2px;
      position: relative;
      color: ${theme.colors.alpha.black[100]};

      &.Mui-selected,
      &:hover,
      &.MuiButtonBase-root:active {
          background: ${theme.colors.primary.lighter};
          color: ${theme.colors.primary.main};
      }

      &:last-child {
          margin-bottom: 0;
      }
    }
`
);

const MenuUserBox = styled(Box)(
    ({ theme }) => `
        background: ${alpha(theme.colors.alpha.black[100], 0.08)};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
    ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
`
);

const UserBoxLabel = styled(Typography)(
    ({ theme }) => `
        font-weight: ${theme.typography.fontWeightBold};
        color: ${theme.palette.secondary.main};
        display: block;
`
);

const UserBoxDescription = styled(Typography)(
    ({ theme }) => `
        color: ${theme.palette.secondary.light}
`
);

function HeaderUserbox() {
    const { t } = useTranslation();
    const theme = useTheme();

    // start: redux state --------------------------
    const authState = useSelector((state) => {
        return state.auth;
    });

    const { user } = authState;
    // end: redux state -----------------------------

    const authUser = {
        name: user?.properName,
        avatar: null,
        jobTitle: user?.jobTitle,
    };

    const ref = useRef(null);
    const [isOpen, setOpen] = useState(false);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleLogout = () => {
        try {
            handleClose();

            logout();

            if (window.Marker) {
                window.Marker.hide();
            }

            window.location.href = "/auth/signin";

        } catch (err) {
            console.error(err);
        }
    };

    return (
        <>
            <UserBoxButton color="primary" ref={ref} onClick={handleOpen}>
                <UserAvatar alt={authUser.name} src={authUser.avatar} />
            </UserBoxButton>
            <Popover
                disableScrollLock
                anchorEl={ref.current}
                onClose={handleClose}
                open={isOpen}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
            >
                <MenuUserBox
                    sx={{
                        minWidth: 210
                    }}
                    display="flex"
                >
                    <Avatar variant="rounded" alt={authUser.name} src={authUser.avatar} />
                    <UserBoxText>
                        <UserBoxLabel variant="body1">{authUser.name}</UserBoxLabel>
                        <UserBoxDescription variant="body2">
                            {authUser.jobTitle}
                        </UserBoxDescription>
                    </UserBoxText>
                </MenuUserBox>
                <Divider
                    sx={{
                        mb: 0
                    }}
                />
                <MenuListWrapperPrimary disablePadding>
                    <MenuItem>
                        <ListItemText
                            primaryTypographyProps={{
                                variant: 'h5'
                            }}
                            primary={t('My account')}
                            onClick={() => {
                                return notFinishedMsg();
                            }}
                        />
                        <ChevronRightTwoToneIcon
                            sx={{
                                color: `${theme.colors.alpha.black[30]}`,
                                opacity: 0.8
                            }}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ListItemText
                            primaryTypographyProps={{
                                variant: 'h5'
                            }}
                            primary={t('Profile settings')}
                            onClick={() => {
                                return notFinishedMsg();
                            }}
                        />
                        <Box display="flex" alignItems="center">
                            <DotLegend
                                style={{
                                    background: `${theme.colors.warning.main}`
                                }}
                            />
                            <ChevronRightTwoToneIcon
                                sx={{
                                    ml: 1,
                                    color: `${theme.colors.alpha.black[30]}`,
                                    opacity: 0.8
                                }}
                            />
                        </Box>
                    </MenuItem>
                    <MenuItem>
                        <ListItemText
                            primaryTypographyProps={{
                                variant: 'h5'
                            }}
                            primary={t('Active tasks')}
                            onClick={() => {
                                return notFinishedMsg();
                            }}
                        />
                        <ChevronRightTwoToneIcon
                            sx={{
                                color: `${theme.colors.alpha.black[30]}`,
                                opacity: 0.8
                            }}
                        />
                    </MenuItem>
                </MenuListWrapperPrimary>
                <Divider />
                <Box m={1}>
                    <Button color="primary" fullWidth onClick={handleLogout}>
                        <LockOpenTwoToneIcon
                            sx={{
                                mr: 1
                            }}
                        />
                        {t('Sign out')}
                    </Button>
                </Box>
            </Popover>
        </>
    );
}

export default HeaderUserbox;
